import React from 'react';
import moment from 'moment-timezone';
import { appNames, types } from '../../constants';
import { formatUrlForWrapping } from '../../utils/helper';
import { View } from '@react-pdf/renderer';
import { Column, Label, Row, SectionHeader, Divider, SectionTitle, Value, LinkValue } from '../components';

const SuppDocsRequired = ({ type, docs, docTypes, shareHoldRequired, auditAnnualReportRequired, logoRequired, letterResignRequired, boardRequired, changeCtrlRequired, appApptCustDocRequired, crtOrdGrntCustRequired, draftMgCertRequired }) => {
  const isDNS = type === appNames.DNS;
  const shareHolderDoc = shareHoldRequired && docs ? docs.find(doc => doc.typeId === types.SHAREH_LIST) : {};
  const auditAnnualReportDoc = auditAnnualReportRequired && docs ? docs.find(doc => doc.typeId === types.AUDITED_ANNUAL_REPORT) : {};
  const logoDoc = logoRequired && docs ? docs.find(doc => doc.typeId === types.LOGO) : {};
  const letterResignDoc = letterResignRequired && docs ? docs.find(doc => doc.typeId === types.LETTER_RESIGN) : {};
  const boardDoc = boardRequired && docs ? docs.find(doc => doc.typeId === types.BOARD) : {};
  const changeCtrlDoc = changeCtrlRequired && docs ? docs.find(doc => doc.typeId === types.DSC_CHG_CTRL_EVT) : {};
  const appApptCustDoc = appApptCustDocRequired && docs ? docs.find(doc => doc.typeId === types.APP_APPT_CUST) : {};
  const crtOrdGrntCustDoc = crtOrdGrntCustRequired && docs ? docs.find(doc => doc.typeId === types.CRT_ORD_GRNT_CUST) : {};
  const draftMgCertDoc = draftMgCertRequired && docs ? docs.find(doc => doc.typeId === types.DRAFT_MG_CERT) : {};
  const docTypesMap = docTypes && new Map(docTypes.map(doc => [doc.typeId, doc.description]));

  const DocPassword = ({ doc }) => {
    return doc && doc.attributes && doc.attributes.filePassword && doc.attributes.filePassword.length > 0 ? <>
      <Row>
        <Column>
          <Label text='DOCUMENT PASSWORD' />
          <Value text={doc.attributes.filePassword} />
        </Column>
      </Row>
    </> : null;
  };

  const LetterResign = () => <>
    <SectionTitle text={docTypesMap.get(types.LETTER_RESIGN)} />
    <Row>
      <Column size='50'>
        <Label text='UPLOAD DATE' />
        <Value text={letterResignDoc && letterResignDoc.attributes && moment(letterResignDoc.created).format('LL')} />
      </Column>
      <Column size='50'>
        <Label text='DOCUMENT DATE' />
        <Value text={letterResignDoc && letterResignDoc.attributes && moment(letterResignDoc.attributes.asOfDate).format('LL')} />
      </Column>
    </Row>
    <Row>
      <Column>
        <Label text='FULL NAME OF INDIVIDUAL LISTED ON DOCUMENT' />
        <Value text={letterResignDoc && letterResignDoc.attributes && letterResignDoc.attributes.fullName} />
      </Column>
    </Row>
    <DocPassword doc={letterResignDoc} />
    <Divider />
  </>;

  return (
    <>
      <View fixed>
        <SectionHeader text={`SUPPORTING DOCUMENTS${!isDNS ? ' | REQUIRED' : ''}`} />
      </View>
      <View>
        {shareHoldRequired && <>
          <SectionTitle text={docTypesMap.get(types.SHAREH_LIST)} />
          <Row>
            <Column size='50'>
              <Label text='UPLOAD DATE' />
              <Value text={shareHolderDoc && shareHolderDoc.attributes && moment(shareHolderDoc.created).format('LL')} />
            </Column>
            <Column size='50'>
              <Label text='DOCUMENT DATE' />
              <Value text={shareHolderDoc && shareHolderDoc.attributes && moment(shareHolderDoc.attributes.asOfDate).format('LL')} />
            </Column>
          </Row>
          <DocPassword doc={shareHolderDoc} />
          <Divider />
        </>}
        {!isDNS && auditAnnualReportDoc && <>
          <SectionTitle text={docTypesMap.get(types.AUDITED_ANNUAL_REPORT)} />
          <Row>
            <Column>
              <Label text='UPLOAD DATE' />
              <Value text={auditAnnualReportDoc && auditAnnualReportDoc.attributes && moment(auditAnnualReportDoc.created).format('LL')} />
            </Column>
          </Row>
          <Row>
            {auditAnnualReportDoc?.attributes?.isFileLink && <Column>
              <Label text='DOCUMENT LINK' />
              <LinkValue src={auditAnnualReportDoc.fileLink} text={formatUrlForWrapping(auditAnnualReportDoc.fileLink)} />
            </Column>}
          </Row>
          <DocPassword doc={auditAnnualReportDoc} />
          <Divider />
        </>}
        {draftMgCertRequired && <>
          <SectionTitle text={docTypesMap.get(types.DRAFT_MG_CERT)} />
          <Row>
            <Column size='50'>
              <Label text='UPLOAD DATE' />
              <Value text={draftMgCertDoc && draftMgCertDoc.attributes && moment(draftMgCertDoc.created).format('LL')} />
            </Column>
            <Column size='50'>
              <Label text='DOCUMENT DATE' />
              <Value text={draftMgCertDoc && draftMgCertDoc.attributes && moment(draftMgCertDoc.attributes.asOfDate).format('LL')} />
            </Column>
          </Row>
          <DocPassword doc={draftMgCertDoc} />
          <Divider />
        </>}
        {changeCtrlRequired && <>
          <SectionTitle text={docTypesMap.get(types.DSC_CHG_CTRL_EVT)} />
          <Row>
            <Column size='50'>
              <Label text='UPLOAD DATE' />
              <Value text={changeCtrlDoc && changeCtrlDoc.attributes && moment(changeCtrlDoc.created).format('LL')} />
            </Column>
            <Column size='50'>
              <Label text='DOCUMENT DATE' />
              <Value text={changeCtrlDoc && changeCtrlDoc.attributes && moment(changeCtrlDoc.attributes.asOfDate).format('LL')} />
            </Column>
          </Row>
          <DocPassword doc={changeCtrlDoc} />
          <Divider />
        </>}
        {appApptCustDocRequired && <>
          <SectionTitle text={docTypesMap.get(types.APP_APPT_CUST)} />
          <Row>
            <Column size='50'>
              <Label text='UPLOAD DATE' />
              <Value text={appApptCustDoc && appApptCustDoc.attributes && moment(appApptCustDoc.created).format('LL')} />
            </Column>
            <Column size='50'>
              <Label text='DOCUMENT DATE' />
              <Value text={appApptCustDoc && appApptCustDoc.attributes && moment(appApptCustDoc.attributes.asOfDate).format('LL')} />
            </Column>
          </Row>
          <DocPassword doc={appApptCustDoc} />
          <Divider />
        </>}
        {crtOrdGrntCustRequired && <>
          <SectionTitle text={docTypesMap.get(types.CRT_ORD_GRNT_CUST)} />
          <Row>
            <Column size='50'>
              <Label text='UPLOAD DATE' />
              <Value text={crtOrdGrntCustDoc && crtOrdGrntCustDoc.attributes && moment(crtOrdGrntCustDoc.created).format('LL')} />
            </Column>
            <Column size='50'>
              <Label text='DOCUMENT DATE' />
              <Value text={crtOrdGrntCustDoc && crtOrdGrntCustDoc.attributes && moment(crtOrdGrntCustDoc.attributes.asOfDate).format('LL')} />
            </Column>
          </Row>
          <DocPassword doc={crtOrdGrntCustDoc} />
          <Divider />
        </>}
        {(letterResignRequired && appApptCustDocRequired) && <LetterResign />}
        {boardRequired && <>
          <SectionTitle text={docTypesMap.get(types.BOARD)} />
          <Row>
            <Column size='50'>
              <Label text='UPLOAD DATE' />
              <Value text={boardDoc && boardDoc.attributes && moment(boardDoc.created).format('LL')} />
            </Column>
            <Column size='50'>
              <Label text='DOCUMENT DATE' />
              <Value text={boardDoc && boardDoc.attributes && moment(boardDoc.attributes.asOfDate).format('LL')} />
            </Column>
          </Row>
          <DocPassword doc={boardDoc} />
          <Divider />
        </>}
        {(letterResignRequired && !appApptCustDocRequired) && <LetterResign />}
        {logoRequired && <>
          <SectionTitle text={docTypesMap.get(types.LOGO)} />
          <Row>
            <Column>
              <Label text='UPLOAD DATES' />
              <Value text={logoDoc && moment(logoDoc.created).format('LL')} />
            </Column>
          </Row>
        </>}
      </View>
    </>
  );
};

export default SuppDocsRequired;
